/**
 * ? note(@oussaz) - camelCase/PascalCase are lowerCased first
 *
 * @param str to convert to kebab case
 * @returns opiniated kebabCase string
 */
export function kebabCase(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

/**
 * Converts a string to kebab-case but preserves existing underscores
 *
 * @param str to convert to kebab case with underscores allowed
 * @returns kebab-case string with underscores preserved
 */
export function kebabCaseWithUnderscores(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9_]/g, '-');
}

// Used for schema keys where the usual AIP doesn't matter
export function kebabSnakeOrCamelCase(str: string) {
  return str.replace(/[^a-zA-Z0-9_]/g, '-');
}

export const isValidEmail = (email: string): boolean => {
  return !!String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

/**
 * Converts a name to a valid AIP-122 resource ID.
 * Resource IDs must:
 * - Be 1-63 characters
 * - Match pattern: [a-z]([a-z0-9-]{0,61}[a-z0-9])?
 * - Start with a lowercase letter
 * - End with a lowercase letter or digit
 * - Only contain lowercase letters, digits, and hyphens
 *
 * @param name - The name to convert to a resource ID
 * @returns AIP-122 compliant resource ID
 */
export const slugify = (name: string): string => {
  // Convert to lowercase and replace spaces/underscores with hyphens
  let id = name
    .toLowerCase()
    .trim()
    .replaceAll(/[\s_]+/g, '-');

  // Remove all characters that aren't lowercase letters, digits, or hyphens
  id = id.replaceAll(/[^a-z0-9-]/g, '');

  // Remove consecutive hyphens
  id = id.replaceAll(/-+/g, '-');

  // Ensure it starts with a letter
  id = id.replace(/^[^a-z]+/, '');

  // Ensure it ends with a letter or digit (remove trailing hyphens)
  id = id.replace(/-+$/, '');

  // Limit to 63 characters
  if (id.length > 63) {
    id = id.substring(0, 63);
    // Re-trim trailing hyphens after truncation
    id = id.replace(/-+$/, '');
  }

  return id;
};

/**
 * Counts the number of words in a string.
 * Words are separated by whitespace.
 *
 * @param str - The string to count words in
 * @returns Number of words in the string
 */
export const countWords = (str: string): number => {
  return str.trim().split(/\s+/).filter(Boolean).length;
};
